import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, queryAssignedElements, state} from "lit/decorators.js";
import Styles from "./macherbusVoting.lit.scss";
import {resolve} from "../../container";
import {MacherbusStorage} from "./macherbusStorage";
import {templateContaining} from "../../common/utils/html";
import {AutoInitializing} from "../../common/elements";
import {Dictionary} from "../../page/elements/dictionary";
import type {EopMacherbusProject} from "./macherbusProject";
import {MACHERBUS_PROJECT_VOTE_EVENT, type MacherbusProjectVoteEvent, type MacherbusProjectVoteEventData} from "./macherbusEvents";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";


const REQUIRED_VOTE_COUNT = 3;

@customElement("eop-macherbus-voting")
export class EopMacherbusVoting extends AutoInitializing(LitElement) {

    public static readonly styles = Styles;

    @property({attribute: "voting-id"})
    public votingId: string;
    @state()
    private votedProjects: string[];
    @queryAssignedElements({slot: "macherbus-project"})
    private projects: EopMacherbusProject[];

    private checkoutLayer: string;
    private dictionary: Dictionary;

    public constructor(private macherbusStorage: MacherbusStorage = resolve(MacherbusStorage)) {
        super();
        this.votedProjects = [];
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);

        this.addEventListener(MACHERBUS_PROJECT_VOTE_EVENT, (event: Event) => {
            const eventDetails = (event as MacherbusProjectVoteEvent).detail;
            this.votedProjects = this.newVotedProjects(eventDetails);
            this.macherbusStorage.saveVotes(this.votingId, this.votedProjects);
        });

        const checkoutLayerTemplate = this.querySelector<HTMLTemplateElement>(".macherbus-checkout-layer-template");
        if (checkoutLayerTemplate !== null) {
            this.checkoutLayer = checkoutLayerTemplate.innerHTML;
        }
    }

    private newVotedProjects(update: MacherbusProjectVoteEventData): string[] {
        const newVotedProjects = Array.from(this.votedProjects);
        if (update.voted) {
            newVotedProjects.push(update.projectId);
        } else {
            newVotedProjects.removeAll(update.projectId);
        }
        return newVotedProjects;
    }

    public render(): TemplateResult {
        const checkoutContent = templateContaining(this.checkoutLayer);
        checkoutContent.setAttribute("data-id", "content");
        const finalizeVotingLabel = this.dictionary.translate("FINALIZE_VOTING_LABEL");
        return html`
            <slot name="macherbus-project" class="macherbus-projects"></slot>
            <div class="action-footer">
                <div class="invalid-vote-message">${this.invalidVoteMessage()}</div>
                <eop-inline-modal-dialog-button
                        ?disabled-button=${!this.allVoted()}
                        style="primary"
                        label=${finalizeVotingLabel}
                        ${trackingButtonInteract(finalizeVotingLabel)}
                >
                    ${checkoutContent}
                </eop-inline-modal-dialog-button>
            </div>
        `;
    }

    private invalidVoteMessage(): string {
        const voteCount = this.votedProjects.length;
        if (voteCount === REQUIRED_VOTE_COUNT) {
            return "";
        }

        return this.dictionary.translate("WRONG_NUMBER_OF_VOTES_MESSAGE", voteCount === 1 ? "1" : "_", {"[ACTUAL]": voteCount});
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.append(...Array.from(this.projects).shuffle());

        const voted = this.macherbusStorage.readVotes(this.votingId);
        this.projects
            .filter(p => voted.includes(p.projectId))
            .forEach(p => p.setVoted(true));
    }

    private allVoted(): boolean {
        return this.projects.length > 0
            && this.votedProjects.length === REQUIRED_VOTE_COUNT;
    }
}